import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useTranslation } from 'react-i18next';
import React from 'react';
import { setTitle } from 'utils/head';
import { Box } from '@mui/material';
import { getAddObjectsToLessonMutationOptions, getLessonMutationOptions, getRemoveObjectsFromLessonMutationOptions, useLessonsOverviewQueryApi, useSchoolYearsWithCourseListQueryApi, useUpdateLessonMutation, } from 'api';
import { LoadingWrapper } from 'components/custom';
import NotesDialog from 'pages/lesson/Notes/NotesDialog';
import CourseSelect from './CourseSelect';
import CourseListSource from './CourseListSource';
import CourseListTarget from './CourseListTarget';
import { useResizeObserver } from 'utils/resize_observer';
import RemoveObjectsDialog from './RemoveObjectsDialog';
import { enqueueSnackbar } from 'notistack';
import { useAddObjectsToLessonMutation, useRemoveObjectsFromLessonMutation, } from 'api/generated';
import OverwriteLessonsDialog from './OverwriteLessonsDialog';
const CoursePlanning = () => {
    const { t } = useTranslation('coursePlanning');
    const [position, positionRef] = useResizeObserver();
    const [sourceCourseId, setSourceCourseId] = React.useState('');
    const [targetCourseId, setTargetCourseId] = React.useState('');
    const [selectedSourceLessonIds, setSelectedSourceLessonIds] = React.useState([]);
    const [lessonToEdit, setLessonToEdit] = React.useState(null);
    const [lessonToRemoveObjectsFrom, setLessonToRemoveObjectsFrom] = React.useState(null);
    const [overwriteLessonsDialogData, setOverwriteLessonsDialogData] = React.useState(null);
    const schoolYearsStatus = useSchoolYearsWithCourseListQueryApi();
    const sourceLessonsStatus = useLessonsOverviewQueryApi({ courseId: sourceCourseId }, { skip: !sourceCourseId });
    const targetLessonsStatus = useLessonsOverviewQueryApi({ courseId: targetCourseId }, { skip: !targetCourseId });
    const [updateLesson, statusUpdateLesson] = useUpdateLessonMutation();
    const [unlinkObject, statusUnlinkObject] = useRemoveObjectsFromLessonMutation();
    const [assignObjects, statusAssignObjects] = useAddObjectsToLessonMutation();
    React.useEffect(() => {
        setTitle(t('title'));
    }, [t]);
    const onSourceCourseChange = (e) => {
        setSourceCourseId(e.target.value);
        setSelectedSourceLessonIds([]);
    };
    const onTargetCourseChange = (e) => {
        setTargetCourseId(e.target.value);
    };
    const handleToggleLessonCheckbox = (lessonId) => {
        if (selectedSourceLessonIds.includes(lessonId)) {
            setSelectedSourceLessonIds(selectedSourceLessonIds.filter((id) => id !== lessonId));
        }
        else {
            setSelectedSourceLessonIds([...selectedSourceLessonIds, lessonId]);
        }
    };
    const sendTransferLessonsToServer = (transferData, overwriteObjects) => {
        const allRequests = [];
        for (const transfer of transferData) {
            if (transfer.sourceNotes !== null) {
                const notesPromise = updateLesson(getLessonMutationOptions({
                    id: transfer.targetId,
                    notes: transfer.sourceNotes,
                }));
                allRequests.push(notesPromise);
            }
            if (overwriteObjects && transfer.objectIdsToRemove.length) {
                const unlinkPromise = unlinkObject(getRemoveObjectsFromLessonMutationOptions(transfer.targetId, transfer.objectIdsToRemove));
                allRequests.push(unlinkPromise);
            }
            if (transfer.objectIdsToAdd.length) {
                const assignPromise = assignObjects(getAddObjectsToLessonMutationOptions(transfer.targetId, transfer.objectIdsToAdd));
                allRequests.push(assignPromise);
            }
        }
        Promise.allSettled(allRequests)
            .then((results) => {
            let good = 0;
            let bad = 0;
            results.forEach((result) => {
                if (result.status === 'fulfilled') {
                    good++;
                }
                else {
                    bad++;
                }
            });
            if (bad === 0) {
                const message = transferData.length === 1
                    ? 'messageTransferSingleSuccess'
                    : 'messageTransferMultipleSuccess';
                enqueueSnackbar(t(message), { variant: 'success' });
            }
            else if (good === 0) {
                enqueueSnackbar(t('messageTransferError'), { variant: 'error' });
            }
            else {
                enqueueSnackbar(t('messageTransferPartiallyError'), {
                    variant: 'error',
                });
            }
        })
            .catch(() => {
            enqueueSnackbar(t('messageTransferError'), { variant: 'error' });
        });
        setSelectedSourceLessonIds([]);
    };
    const handleTransferLessons = (guiReportedSourceLessonId, guiReportedTargetLessonId) => {
        const sourceLessonIds = selectedSourceLessonIds.length
            ? selectedSourceLessonIds
            : [guiReportedSourceLessonId];
        const sourceLessons = (sourceLessonsStatus.data?.lessons ?? []).filter((l) => sourceLessonIds.includes(l.id));
        const targetLessons = targetLessonsStatus.data?.lessons ?? [];
        const firstTargetIndex = targetLessons.findIndex((l) => l.id === guiReportedTargetLessonId);
        const lessonsToConfirm = [];
        const transferData = sourceLessons.map((source, index) => {
            const target = targetLessons[firstTargetIndex + index];
            const objectIdsToAdd = source.objects
                .filter((a) => !target.objects.some((b) => a.id === b.id))
                .map((o) => o.id);
            const objectIdsToRemove = target.objects
                .filter((a) => !source.objects.some((b) => a.id === b.id))
                .map((o) => o.id);
            if (objectIdsToRemove.length) {
                lessonsToConfirm.push(target.formatedDateTimePeriod);
            }
            return {
                objectIdsToAdd,
                objectIdsToRemove,
                sourceNotes: source.notes === target.notes ? null : source.notes,
                targetId: target.id,
            };
        });
        if (lessonsToConfirm.length) {
            setOverwriteLessonsDialogData({ lessonsToConfirm, transferData });
        }
        else {
            sendTransferLessonsToServer(transferData, false);
        }
    };
    return (_jsxs(_Fragment, { children: [_jsx(Box, { sx: {
                    height: '100%',
                    maxHeight: '100%',
                    position: 'relative',
                }, children: _jsx(LoadingWrapper, { status: schoolYearsStatus, mutation: [
                        statusUpdateLesson,
                        statusUnlinkObject,
                        statusAssignObjects,
                    ], overlay: true, children: _jsxs(Box, { sx: {
                            bottom: 0,
                            columnGap: '2rem',
                            display: 'grid',
                            gridTemplateColumns: 'minmax(0, 50%) minmax(0, 50%)',
                            gridTemplateRows: 'auto 1fr',
                            left: 0,
                            overflow: 'hidden',
                            position: 'absolute',
                            right: 0,
                            top: 0,
                        }, children: [_jsx(CourseSelect, { name: "source", onChange: onSourceCourseChange, schoolYearsStatus: schoolYearsStatus, selectedId: sourceCourseId }), _jsx(CourseSelect, { name: "target", onChange: onTargetCourseChange, schoolYearsStatus: schoolYearsStatus, selectedId: targetCourseId }), _jsx(Box, { position: "relative", overflow: "hidden", children: _jsx(LoadingWrapper, { status: sourceLessonsStatus, overlay: true, children: _jsx(CourseListSource, { courseId: sourceCourseId, lessons: sourceLessonsStatus.data?.lessons, selectedLessonIds: selectedSourceLessonIds, onToggleLessonCheckbox: handleToggleLessonCheckbox, handleTransferLessons: handleTransferLessons }) }) }), _jsx(Box, { ref: positionRef, position: "relative", overflow: "hidden", children: _jsx(LoadingWrapper, { status: targetLessonsStatus, overlay: true, children: _jsx(CourseListTarget, { courseId: targetCourseId, lessons: targetLessonsStatus.data?.lessons, offsetLeft: position.left, offsetTop: position.top, selectedLessons: selectedSourceLessonIds, startEditLesson: setLessonToEdit, setLessonToRemoveObjectsFrom: setLessonToRemoveObjectsFrom, handleTransferLessons: handleTransferLessons }) }) })] }) }) }), _jsx(NotesDialog, { activeLesson: lessonToEdit, close: () => {
                    setLessonToEdit(null);
                } }), _jsx(RemoveObjectsDialog, { closeDialog: () => {
                    setLessonToRemoveObjectsFrom(null);
                }, activeLesson: lessonToRemoveObjectsFrom }), _jsx(OverwriteLessonsDialog, { data: overwriteLessonsDialogData, setData: setOverwriteLessonsDialogData, sendTransferLessonsToServer: sendTransferLessonsToServer })] }));
};
export default CoursePlanning;
